import React, { useEffect, useState } from 'react'
import { NavLink as RouterLink, useHistory, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/styles'
import { ListItem, Button, Collapse, Tooltip } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    padding: theme.spacing(0.25, 0)
  },
  itemLeaf: {
    display: 'flex',
    padding: theme.spacing(0.25, 0)
  },
  button: {
    position: 'relative',
    minHeight: 38,
    padding: theme.spacing(0, 1.25),
    borderRadius: 8,
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    color: '#42535f',
    fontSize: 12,
    fontWeight: 500,
    transition: 'background .15s ease, color .15s ease',
    '&:hover': {
      backgroundColor: '#f7f9fa'
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  buttonLeaf: {
    position: 'relative',
    minHeight: 38,
    padding: theme.spacing(0, 1.25),
    borderRadius: 8,
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    color: '#42535f',
    fontSize: 12,
    fontWeight: 500,
    transition: 'background .15s ease, color .15s ease',
    '&.depth-0': {
      fontWeight: 500
    },
    '&:hover': {
      backgroundColor: '#f7f9fa'
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  indentedButton: ({ depth }) => ({
    paddingLeft: depth > 0 ? 32 + 8 * depth : 10
  }),
  icon: {
    fontSize: 17,
    color: '#647984',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1.25),
    flexShrink: 0
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 14,
    width: 14,
    transition: 'transform .15s ease'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  active: {
    backgroundColor: '#fff4f5',
    color: theme.palette.primary.main,
    fontWeight: 700,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      width: 3,
      height: 18,
      borderRadius: 3,
      backgroundColor: theme.palette.primary.main,
      transform: 'translateY(-50%)'
    },
    '& $icon': {
      color: theme.palette.primary.main
    }
  },
  collapsedButton: {
    justifyContent: 'center',
    padding: 0,
    '& $icon': {
      marginRight: 0
    },
    '&::before': {
      left: 3
    }
  }
}))

function NavItem ({
  isExternalUrl,
  title,
  href,
  activeOn,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  label: Label,
  collapsed,
  onExpand,
  ...rest
}) {
  const classes = useStyles({ depth })
  const history = useHistory()
  const location = useLocation()
  const [open, setOpen] = useState(openProp)

  // Arrivando su una pagina del gruppo (anche da un'altra sezione) il gruppo si apre.
  useEffect(() => {
    if (openProp) setOpen(true)
  }, [openProp])

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const buttonClassName = clsx(classes.button, classes.indentedButton, collapsed && classes.collapsedButton)
  const leafButtonClassName = clsx(classes.buttonLeaf, classes.indentedButton, `depth-${depth}`, collapsed && classes.collapsedButton)

  const withTooltip = (element) => collapsed
    ? <Tooltip placement='right' title={title}>{element}</Tooltip>
    : element

  if (children) {
    return withTooltip(
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
      >
        <Button
          aria-expanded={collapsed ? false : open}
          aria-label={title}
          className={buttonClassName}
          onClick={() => {
            // A compact menu exposes only root icons; expanding first keeps nested navigation reachable.
            if (collapsed) {
              onExpand()
              return
            }
            handleToggle()
          }}
        >
          {Icon && <Icon className={classes.icon} />}
          {!collapsed && title}
          {!collapsed && (open ? (
            <ExpandLessIcon
              className={classes.expandIcon}
              color='inherit'
            />
          ) : (
            <ExpandMoreIcon
              className={classes.expandIcon}
              color='inherit'
            />
          ))}
        </Button>
        {!collapsed && <Collapse in={open}>{children}</Collapse>}
      </ListItem>
    )
  }

  return withTooltip(
    <ListItem
      {...rest}
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
    >
      {
        isExternalUrl
          ? <Button
            activeClassName={classes.active}
            aria-label={title}
            className={leafButtonClassName}
            onClick={() => { window.location.href = href }}
          >
            {Icon && <Icon className={classes.icon} />}
            {!collapsed && title}
            {!collapsed && Label && (
              <span className={classes.label}>
                <Label />
              </span>
            )}
          </Button>
          : <Button
            activeClassName={classes.active}
            aria-label={title}
            className={leafButtonClassName}
            component={RouterLink}
            isActive={(match, { pathname }) => [href, ...(activeOn || [])].some((path) => pathname === path || pathname.startsWith(`${path}/`))}
            onClick={(event) => {
              // Su route figlie (es. /scf/handleSuppliers/:id) torna alla route base del menu
              if (href && location.pathname.startsWith(`${href}/`)) {
                event.preventDefault()
                history.push(href)
              }
            }}
            to={href}
          >
            {Icon && <Icon className={classes.icon} />}
            {!collapsed && title}
            {!collapsed && Label && (
              <span className={classes.label}>
                <Label />
              </span>
            )}
          </Button>
      }
    </ListItem>
  )
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  // Altri indirizzi su cui la voce resta selezionata (es. pagine aperte da quella voce)
  activeOn: PropTypes.arrayOf(PropTypes.string),
  icon: PropTypes.any,
  label: PropTypes.any,
  open: PropTypes.bool,
  onExpand: PropTypes.func,
  title: PropTypes.string.isRequired
}

NavItem.defaultProps = {
  activeOn: undefined,
  collapsed: false,
  onExpand: () => {},
  open: false
}

export default NavItem
